const PayyoPaymentStatus = require('@/lib/PaymentMethods/Payyo/Enums/PayyoPaymentStatus');
const PayyoSocketResponseType = require('@/lib/PaymentMethods/Payyo/Enums/PayyoSocketResponseType');
const PayyoTransactionType = require('@/lib/PaymentMethods/Payyo/Enums/PayyoTransactionType');
const { toMinorUnit } = require('@/functions/math');

/**
 * @typedef {Object} PaymentMethodCardPresent
 * @property {Object} card_present - The customer is present for the transaction.
 */

/**
 * @typedef {Object} PaymentMethodCard
 * @property {Object} card - The customer is not present this is a CNP / MOTO transaction.
 */

/**
 * @typedef {PaymentMethodCardPresent|PaymentMethodCard} PaymentMethods
 */

class PayyoTransaction {
    /**
     * PayyoTransaction holds all the information about a SALE or REFUND request made to the Payyo API.
     * As the transaction is processed by Payyo we update this object to store the current state.
     *
     * @param {number} amount
     */
    constructor(amount) {
        if (!amount || typeof amount !== 'number') {
            throw new Error('PayyoTransaction requires amount to be a number');
        }

        /**
         * @type {number}
         */
        this.amountInMajorUnits = amount;

        /**
         * @type {number}
         */
        this.amountInMinorUnits = toMinorUnit(amount);

        /**
         * 35 character string matching regex ^py_[a-zA-Z0-9]{32}$
         *
         * @type {string|null}
         */
        this.paymentId = null;

        /**
         * The DigiTickets payment we are refunding.
         *
         * @type {Payment}
         */
        this.originalPayment = null;

        /**
         * 35 character string matching regex ^re_[a-zA-Z0-9]{32}$
         *
         * @type {string|null}
         */
        this.refundId = null;

        /**
         * @type {string|null}
         */
        this.thirdPartyID = null;

        /**
         * Data from an HTTP Axios Response or a websocket message.
         *
         * @type {Object}
         */
        this.latestResponse = {
            payment: null,
            refund: null,
        };

        /**
         * @type {?string}
         */
        this.status = null;

        /**
         * This value is used to check if the status of the transaction in progress has changed.
         *
         * @type {?string}
         */
        this.lastStatus = null;

        /**
         * A transaction is finished if it successfully completed, failed, or it was cancelled.
         * It can be used as a flag to enable or disable functionality in the UI.
         *
         * Set this property from PayyoPaymentMethod.vue DO NOT set it from within this class.
         *
         * @type {boolean}
         */
        this.isFinished = false;

        /**
         * A more detailed message than status that should be shown to the operator if an additional action
         * such as additional verification is required.
         *
         * This is plain text, not a lang key. The comes from responses from Payyo.
         *
         * @type {?string}
         */
        this.userMessage = null;

        /**
         * Should we tell the payment terminal to prompt the user for tips?
         *
         * @type {boolean}
         */
        this.promptForTips = false;

        /**
         * When status === TRANSACTION_FINISHED what is the result?
         * See PayyoTransactionResultStatus.js
         *
         * @type {{message: ?string, class: ?string}}
         */
        this.transactionResult = {
            message: null,
            class: null,
        };

        /**
         * The description that gets displayed on the Payyo Dashboard.
         * This will be in the format 'Branch Name (ProPoint)'.
         *
         * @type {string}
         */
        this.description = '';

        /**
         * @see https://docs.pos.payyo.ch/docs/resources/payments/#create-payment
         *
         * @type {PaymentMethods}
         */
        this.payment_methods = {};
    }

    /**
     * DigiTickets payment.thirdPartyID used as payyo.reference
     *
     * @param {string} thirdPartyID
     */
    setThirdPartyID(thirdPartyID) {
        if (!thirdPartyID) {
            throw new Error('thirdPartyID is required');
        }

        if (this.thirdPartyID && thirdPartyID !== this.thirdPartyID) {
            throw new Error('thirdPartyID ' + thirdPartyID + ' does not match existing thirdPartyID ' + this.thirdPartyID);
        }

        this.thirdPartyID = thirdPartyID;
    }

    /**
     * Should we tell the payment terminal to prompt the user for tips?
     *
     * @param {boolean} promptForTips
     */
    setPromptForTips(promptForTips) {
        // Let's not worry about errors and just cast to boolean in the event that the input is stupid.
        this.promptForTips = !!promptForTips;
    }

    /**
     * @param {string} description
     */
    setDescription(description) {
        this.description = description + ' (ProPoint)';
    }

    /**
     * @see https://docs.pos.payyo.ch/docs/resources/payments/#create-payment
     *
     * @param {PayyoTransactionType} type
     */
    setPaymentMethodsType(type) {
        if (type === PayyoTransactionType.CARD_MOTO) {
            this.payment_methods = {
                card: {},
            };
            return;
        }

        this.payment_methods = {
            card_present: {},
        };
    }

    clearLatestResponse() {
        this.latestResponse = {
            payment: null,
            refund: null,
        };
    }

    clearTransactionResult() {
        this.transactionResult = {
            message: null,
            class: null,
        };
    }

    addError(status, errorResponse) {
        console.log('addError', status, errorResponse);
        // The transaction has already completed.
        // There is no need to handle the error if the status is already one of the following statuses.
        const statusesToIgnore = [
            PayyoPaymentStatus.SUCCEEDED,
            PayyoPaymentStatus.CANCELED,
            PayyoPaymentStatus.FAILED_UNRECOVERABLE,
        ];
        if (!statusesToIgnore.includes(this.status)) {
            this.status = status;
            if (errorResponse.data.error) {
                this.transactionResult = {
                    message: 'PAYYO.ERROR_STATUS__' + errorResponse.data.error.toUpperCase(),
                    class: 'message-line-danger',
                };
            }
            this.userMessage = errorResponse.data.message;
        }
    }

    /**
     * Either an Axios Response from an HTTP request or a websocket response object.
     *
     * An Axios response looks like this:
     * {
     *     data: {
     *         payment_id: 'some-payment-id',
     *         etc.
     *     }
     * }
     *
     * @see https://axios-http.com/docs/res_schema
     *
     * A websocket message looks like this:
     *
     * {
     *     type: 'payment_created',
     *     payment: {
     *         id: 'some-payment-id-being-refunded',
     *         etc.
     *     },
     *     refund: {
     *         id: 'some-refund-id',
     *         payment_id: 'some-payment-id-being-refunded',
     *         etc.
     *     },
     * }
     *
     * Where type and payment are always required and refund is optional.
     *
     * @param {} response
     */
    addTransactionResponse(response) {
        let payment = null;
        let refund = null;
        let status = null;

        if (this.latestResponse === null) {
            this.latestResponse = {};
        }

        if (!response) {
            throw new Error('Missing Payyo response');
        }

        if (response.data) {
            // This is a response directly from an HTTP request.
            // Note: we ignore the response to an HTTP POST 'refund' request therefore this must be a payment.
            if (!response.data.id) {
                throw new Error('Missing Payyo paymentId in response');
            }
            payment = response.data;
            status = payment.status;

            if (this.paymentId && payment.id !== this.paymentId) {
                console.warn('Payyo HTTP response paymentId ' + payment.id + ' does not match existing paymentId ' + this.paymentId);
                return;
            }

            this.paymentId = payment.id;
        } else {
            if (!response.payment && !response.refund) {
                throw new Error('Missing payment or refund in PayyoWebSocket message', response);
            }

            if (response.payment) {
                payment = response.payment;
                status = payment.status;

                if (this.paymentId && payment.id !== this.paymentId) {
                    // This isn't necessarily bad, for example it could just be an abandoned payment but the important
                    // thing is that we ignore it here and stop processing this message.
                    console.warn('Payyo paymentId ' + payment.id + ' does not match existing paymentId ' + this.paymentId);
                    return;
                }

                this.paymentId = payment.id;
            }

            if (response.refund) {
                refund = response.refund;
                if (this.paymentId && refund.payment_id !== this.paymentId) {
                    console.warn('Payyo refund paymentId ' + response.refund.payment_id + ' does not match existing paymentId ' + this.paymentId);
                    return;
                }

                this.refundId = refund.id;
                status = refund.status;
            }
        }

        // At this point we definitely have either a payment or a refund.

        this.latestResponse.payment = payment;
        this.latestResponse.refund = refund;

        // There should never be a scenario when we have a 2xx response without data and notifications but check anyway.
        if (status) {
            this.status = status;

            if (response.type) {
                this.latestResponse.type = response.type;
                // If there is a response.type this is response from a websocket message not an HTTP request.
                if (response.type === PayyoSocketResponseType.PAYMENT_FAILED) {
                    this.status = PayyoPaymentStatus.FAILED;
                    // This may or may not be completely finished
                    // In order to find that out we need to query the devices endpoint and look at action.status
                    // Therefore we do not set the transactionResult just yet like we do for SUCCESS or CANCELLED.
                    this.userMessage = payment?.last_error?.message;

                    return;
                }
            }

            if (this.status === PayyoPaymentStatus.SUCCEEDED) {
                this.transactionResult = {
                    message: 'PAYYO.TRANSACTION_MESSAGE__SUCCESS',
                    class: 'message-line-success',
                };
                // If there was a previous error message clear it.
                this.userMessage = null;

                return;
            }

            if (this.status === PayyoPaymentStatus.CANCELED) {
                this.transactionResult = {
                    message: 'PAYYO.TRANSACTION_MESSAGE__CANCELLED',
                    class: 'message-line-danger',
                };
                // If there was a previous error message clear it.
                this.userMessage = null;
            }
        } else {
            throw new Error('Missing Payyo status in response');
        }
    }
}

module.exports = PayyoTransaction;
